import axios from 'axios'
import React, { useEffect, useState, useRef } from 'react'
import { Form, InputGroup, Tabs, Tab, Alert, Button } from 'react-bootstrap'
import { getValidPreviousIntentId } from '../../../../utils/functions/getValidPreviousIntentId'
import { updateWhatsAppIntent } from '../../../../utils/functions/updateWhatsAppIntent'
import TriggerDescription from '../TriggerDescription'
import WhatsappWebhookComponent from './WhatsappWebhookComponent'
import useWebhookState from '../../../../utils/hooks/useWebhookState'
import {
  initializeTags,
  addTagToSession,
  removeTagFromSession,
  isTagAvailable
} from '../../../../utils/functions/tagUtils'
import { toast } from 'react-toastify'

interface IProps {
  intent: any
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  closeModal: () => void
  data: any
}

const FILE_UPLOAD_URL = process.env.REACT_APP_FILE_UPLOAD_URL

const Image: React.FC<IProps> = ({ intent, inputJson, setInputJson, closeModal, data }) => {
  const intentIndex = inputJson.findIndex((item) => item.id === intent.id)
  const [key, setKey] = useState('url')
  const [fileName, setFileName] = useState<string>('No file chosen')
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])
  const [imageUrl, setImageUrl] = useState(intent.image?.link || '')
  const [isSaving, setIsSaving] = useState(false)
  const { isWebhookChecked, handleWebhookCheckboxChange, handleFulfillmentChange } =
    useWebhookState(intent, intentIndex, inputJson, setInputJson, null)
  const maxLength = 5000000 // 5MB
  const fileInputRef = useRef<HTMLInputElement>(null)
  const urlInputRef = useRef<HTMLInputElement>(null)

  // ✅ Auto Trigger local state
  const [showAutoTrigger, setShowAutoTrigger] = useState(typeof intent.auto_trigger !== 'undefined')

  useEffect(() => {
    initializeTags()
  }, [])

  const handleChange = (e: any) => {
    const name = e.target.name
    const value = e.target.value
    const newInputJson = [...inputJson]

    if (name === 'trigger' && !!parseInt(value)) {
      newInputJson[intentIndex] = {
        ...intent,
        trigger: parseInt(value)
      }
    }

    if (name === 'tag') {
      const currentTag = intent?.tag || ''

      if (!isTagAvailable(value, currentTag)) {
        toast.error('This tag name is already in use. Please choose another name.')
        e.target.value = currentTag
        return
      }

      // Remove old tag from session if it exists
      if (currentTag && currentTag.trim() !== '') {
        removeTagFromSession(currentTag)
      }

      // Add new tag to session
      if (value && value.trim() !== '') {
        const tagAdded = addTagToSession(value)
        if (tagAdded) {
          newInputJson[intentIndex] = {
            ...intent,
            tag: value.trim()
          }
        }
      } else {
        newInputJson[intentIndex] = {
          ...intent,
          tag: value
        }
      }
    }

    setInputJson(newInputJson)
  }

  const formatSizeInMB = (size: number) => {
    return (size / (1024 * 1024)).toFixed(2) + ' MB'
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    let validFiles: File[] = []

    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].size <= maxLength) {
          validFiles.push(files[i])
          setFileName(files[i].name)
        } else {
          alert(`File ${files[i].name} exceeds the size limit ${formatSizeInMB(maxLength)}.`)
        }
      }

      if (validFiles.length > 0) {
        setImageUrl('')
        if (urlInputRef.current) {
          urlInputRef.current.value = ''
        }
        setUploadedFiles((prev) => [...prev, ...validFiles])
      }
    }
  }

  const handleFileUploadsAndUpdateJson = async () => {
    if (uploadedFiles.length > 0) {
      const uploadPromises = uploadedFiles.map(async (file) => {
        const response = await axios.post(FILE_UPLOAD_URL as string, {
          files: [{ fileName: file.name, mimeType: file.type }]
        })
        const uploadURL = response.data[0].uploadURL
        await axios.put(uploadURL, file)

        return {
          filename: response.data[0].Key,
          url: response.data[0].url
        }
      })

      const fileResponses = await Promise.all(uploadPromises)
      setImageUrl(fileResponses[fileResponses.length - 1].url)

      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        image: {
          ...intent.image,
          link: fileResponses[fileResponses.length - 1].url
        }
      }
      setInputJson(newInputJson)
    }
  }

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      handleFileUploadsAndUpdateJson()
    }
    // eslint-disable-next-line
  }, [uploadedFiles])

  const updateImageLink = (link: string) => {
    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      image: {
        ...intent.image,
        link
      }
    }
    setInputJson(newInputJson)
  }

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value && value !== imageUrl) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
        setFileName('No file chosen')
        setUploadedFiles([])
      }
      setImageUrl(value)
      updateImageLink(value)
    }
  }

  // ✅ Auto Trigger change handler
  const handleAutoTriggerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      auto_trigger: value === 'true'
    }
    setInputJson(newInputJson)
  }

  const handleSave = async () => {
    const token = localStorage.getItem('auth-token')
    const whatsAppIDString = data?.whatsAppIDString
    const previousIntentId = getValidPreviousIntentId(
      intentIndex,
      inputJson,
      data?.WhatsAppInput || []
    )
    setIsSaving(true)
    await updateWhatsAppIntent({
      token,
      whatsAppIDString,
      intent,
      intentJson: inputJson[intentIndex],
      previousIntentId,
      onSuccess: closeModal
    })
  }

  const handleTabChange = (k: string | null) => {
    setKey(k || 'url')
  }

  // ✅ Delete auto trigger field
  const handleDeleteAutoTrigger = () => {
    const newInputJson = [...inputJson]
    delete newInputJson[intentIndex].auto_trigger
    setInputJson(newInputJson)
    setShowAutoTrigger(false)
  }

  return (
    <>
      <h5 className='card-title h4 mb-4'>Image Message</h5>

      {/* ✅ Auto Trigger Section */}
      {!showAutoTrigger ? (
        <div className='my-3'>
          <Button
            variant='primary'
            size='sm'
            onClick={() => {
              setShowAutoTrigger(true)
              const newInputJson = [...inputJson]
              newInputJson[intentIndex] = {
                ...intent,
                auto_trigger: true
              }
              setInputJson(newInputJson)
            }}
          >
            + Add Auto Trigger
          </Button>
          <small className='text-muted d-block mt-1'>
            Clicking will add an auto trigger so the next intent fires immediately.
          </small>
        </div>
      ) : (
        <div className='my-3'>
          <InputGroup>
            <InputGroup.Text>Auto Trigger</InputGroup.Text>
            <Form.Select
              id={`auto_trigger-${intent.id}`}
              name='auto_trigger'
              value={String(intent.auto_trigger)}
              onChange={handleAutoTriggerChange}
            >
              <option value='true'>True</option>
              <option value='false'>False</option>
            </Form.Select>
            <Button variant='danger' size='sm' onClick={handleDeleteAutoTrigger}>
              Delete
            </Button>
          </InputGroup>

          {intent.auto_trigger === false ? (
            <small className='text-danger d-block mt-1'>
              Auto trigger is <strong>False</strong> because this is the last intent. Removing or
              setting to false could create an infinite loop.
            </small>
          ) : (
            <small className='text-muted d-block mt-1'>
              Next intent will be called immediately after this one.
            </small>
          )}
        </div>
      )}

      {/* Image Inputs */}
      <Tabs id='image-input-tabs' activeKey={key} onSelect={handleTabChange} className='mb-3'>
        <Tab eventKey='url' title='From URL'>
          <InputGroup className='my-3'>
            <InputGroup.Text>Image URL</InputGroup.Text>
            <Form.Control
              ref={urlInputRef}
              type='url'
              placeholder='https://example.com/image.jpg'
              defaultValue={imageUrl}
              onBlur={handleUrlBlur}
            />
          </InputGroup>
        </Tab>

        <Tab eventKey='upload' title='Upload Image'>
          <Form.Group className='my-3'>
            <Form.Label>Select image to upload</Form.Label>
            <Form.Control
              ref={fileInputRef}
              type='file'
              accept='image/*'
              onChange={handleFileInputChange}
            />
            {fileName !== 'No file chosen' && (
              <div className='mt-2 text-muted'>Selected file: {fileName}</div>
            )}
          </Form.Group>
        </Tab>
      </Tabs>

      {imageUrl && (
        <div className='text-center my-4'>
          <img
            src={imageUrl}
            alt='Preview'
            style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
          />
        </div>
      )}
      <InputGroup className='my-3'>
        <InputGroup.Text>Tag</InputGroup.Text>
        <Form.Control
          id={`name-${intent.id}`}
          type='text'
          name='tag'
          defaultValue={intent?.tag}
          onBlur={handleChange}
        />
      </InputGroup>
      {!!intent.trigger && (
        <InputGroup className='my-3'>
          <InputGroup.Text>Trigger</InputGroup.Text>
          <Form.Select
            id={`trigger-${intent.id}`}
            name='trigger'
            defaultValue={intent.trigger}
            onChange={handleChange}
          >
            <TriggerDescription inputJson={inputJson} />
          </Form.Select>
        </InputGroup>
      )}

      <div className='d-flex'>
        <label className='me-3 fs-5 fw-bolder mt-3'>
          Webhook
          <div className='form-check form-switch mt-3'>
            <input
              className='form-check-input'
              type='checkbox'
              id='flexSwitchCheckDefault'
              role='switch'
              name='webhookComponents'
              checked={isWebhookChecked}
              onChange={handleWebhookCheckboxChange}
            />
          </div>
        </label>
      </div>
      {/* )} */}

      {isWebhookChecked && (
        <WhatsappWebhookComponent
          inputJson={inputJson}
          intent={intent}
          handleFulfillmentChange={handleFulfillmentChange}
        />
      )}
      <Alert variant='info' className='mt-4'>
        <strong>WhatsApp Image Guidelines:</strong>
        <ul className='mb-0'>
          <li>HTTPS image URLs are required if using links</li>
          <li>Max file size: 5MB</li>
          <li>Supported formats: JPG, PNG</li>
          <li>For uploads, image will be temporarily previewed using a local URL</li>
        </ul>
      </Alert>
      <div className='d-flex ms-auto mt-auto'>
        <button className='btn btn-secondary me-4' onClick={closeModal}>
          <i className='bi bi-eye-fill' /> Preview
        </button>
        <button className='btn btn-primary' onClick={handleSave} disabled={isSaving}>
          <i className='bi bi-save' /> {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </>
  )
}

export default Image
